import React, { useMemo, useState, useEffect, useCallback } from 'react';
import zipcode_to_timezone from 'zipcode-to-timezone';
import _, { pick } from 'lodash';
import { useMutation, useApolloClient } from '@apollo/client';
import { useClientUser } from '@/hooks';
import { useEditModalDynamicFields } from '@/components/ShipmentForm/hooks/modal';
import { price } from '@/components/ShipmentForm/utilities/processOrders';
import { genAttributes, EXCEPTION_TYPES } from '@onward-delivery/core';
import { QUERY_BY_PK_WITH_USER } from '@/graphql/queries/clients';

import { ACTIONS, REQUIRES_CARRIER_INSTRUCTION, INSTRUCTIONS } from './constants';
import { buildReturnClone, CLONE_ITEM, CLONE_ORDER, CLONE_MM_FIELDS } from '@/utilities/orderClone';
import { captureException } from '@sentry/react';
import { post } from '@/utilities/onwardClient';
import { TRIGGER_ORDER_EVENT } from '@/constants/apiRoutes';
import { COMPLETION_SOURCES } from '@/constants/completionSources';
import {
    attemptForException,
    buildFailedClone,
    findUnpricedClone,
    singleAttemptEvents,
    FAILED_ORDER_STATUS,
} from './utils';
import { buildReschedulePatch, isRescheduleReasonValid } from '@/components/Reschedule/utils';
import { ATTEMPT_CLONES_BY_ORIGINAL, CLONE_FAILED_ORDER } from '../../graphql';
import { UPDATE_ORDER_BY_ID } from '@/graphql/mutations/orders';
import { PRICED_RATE_FIELDS } from '@/components/ShipmentForm/utilities/pricingFields';

export const useExceptionActions = ({ order, omitted, items, exception, isAdmin, isShipper, isCarrier }) => {
    return useMemo(() => {
        if (!exception) {
            return [];
        }

        const state = order?.event_state;
        const canReschedule =
            !order.read_only &&
            (['START', 'RECEIVING', 'CD_PENDING_PO', 'WAREHOUSE_RETURN', 'PENDING'].includes(state) || order.oms);
        const canReassign = !order.oms;
        const canSplit = omitted?.itemsByOrderId?.length > 0 && items?.length > 0;
        const canOmit = omitted?.itemsByOrderId?.length > 0;
        const itemKept = !!exception?.item_kept;
        const canRetry = !!exception?.hook_event;

        switch (exception.type) {
            case EXCEPTION_TYPES.SHORTAGE: {
                return [
                    ...(canReschedule && !canSplit ? [ACTIONS.RESCHEDULE] : []),
                    ...(!itemKept && canOmit ? [ACTIONS.OMIT_ITEM] : []),
                    ...(canSplit ? [ACTIONS.SPLIT_ORDERS] : []),
                    ACTIONS.CANCEL_ORDER,
                    ...(isAdmin ? [ACTIONS.RESOLVE_NOOP] : []),
                    ACTIONS.CREATE_WAREHOUSE_RETURN,
                ];
            }
            case EXCEPTION_TYPES.DAMAGED: {
                return [
                    ...(itemKept ? [ACTIONS.CREATE_EXCHANGE] : []),
                    ...(canReschedule ? [ACTIONS.RESCHEDULE] : []),
                    ...(!itemKept && canOmit ? [ACTIONS.OMIT_ITEM] : []),
                    ...(!itemKept && canSplit ? [ACTIONS.SPLIT_ORDERS] : []),
                    ACTIONS.CREATE_RETURN,
                    ACTIONS.CREATE_WAREHOUSE_RETURN,
                    ACTIONS.CANCEL_ORDER,
                    ACTIONS.RESOLVE_NOOP,
                ];
            }
            case EXCEPTION_TYPES.OVERAGE: {
                return [ACTIONS.RESOLVE_NOOP, ACTIONS.CREATE_WAREHOUSE_RETURN];
            }
            case EXCEPTION_TYPES.REFUSAL: {
                return [
                    ...(canReschedule && !canSplit ? [ACTIONS.RESCHEDULE] : []),
                    ...(!itemKept && canOmit ? [ACTIONS.OMIT_ITEM] : []),
                    ...(canSplit ? [ACTIONS.SPLIT_ORDERS] : []),
                    ACTIONS.CANCEL_ORDER,
                    ACTIONS.RESOLVE_NOOP,
                    ACTIONS.CREATE_RETURN,
                    ACTIONS.CREATE_WAREHOUSE_RETURN,
                ];
            }
            case EXCEPTION_TYPES.CUSTOMER_CANCELLATION:
            case EXCEPTION_TYPES.ATTEMPTED_DELIVERY: {
                return [
                    ...(canReschedule ? [ACTIONS.RESCHEDULE] : []),
                    ...(canOmit ? [ACTIONS.OMIT_ITEM] : []),
                    ACTIONS.CANCEL_ORDER,
                    ACTIONS.RESOLVE_NOOP,
                    ACTIONS.CREATE_RETURN,
                    ACTIONS.CREATE_WAREHOUSE_RETURN,
                ];
            }
            case EXCEPTION_TYPES.CARRIER_CANCELLATION: {
                return [
                    ...(canReschedule ? [ACTIONS.RESCHEDULE] : []),
                    ...(canReassign ? [ACTIONS.CHANGE_CARRIER] : []),
                    ...(canRetry ? [ACTIONS.RETRY_EVENT] : []),
                    ACTIONS.RESOLVE_NOOP,
                    ACTIONS.CREATE_RETURN,
                    ACTIONS.CREATE_WAREHOUSE_RETURN,
                ];
            }
            case EXCEPTION_TYPES.WEATHER:
            case EXCEPTION_TYPES.NO_ROOM: {
                return [...(canReschedule ? [ACTIONS.RESCHEDULE] : []), ACTIONS.RESOLVE_NOOP];
            }
            default:
                return [ACTIONS.RESOLVE_NOOP];
        }
    }, [order, items, exception, isAdmin, isShipper, isCarrier]);
};

export const useExceptionInstructions = ({ order, exception, resolution }) => {
    return useMemo(() => {
        if (!exception) {
            return [];
        }

        switch (exception.type) {
            case EXCEPTION_TYPES.DAMAGED:
            case EXCEPTION_TYPES.SHORTAGE: {
                return [
                    INSTRUCTIONS.RETURN_ITEMS,
                    INSTRUCTIONS.SENDER_PICKUP,
                    INSTRUCTIONS.DISPOSE_ITEMS,
                    INSTRUCTIONS.NOOP,
                ];
            }
            default:
                return [INSTRUCTIONS.RETURN_ITEMS, INSTRUCTIONS.SENDER_PICKUP, INSTRUCTIONS.DISPOSE_ITEMS];
        }
    }, [order, exception, resolution]);
};

export const useExceptionState = ({ order, items, exception }) => {
    const { user_id, roles } = useClientUser();
    const [resolution, setAction] = useState(null);
    const [instruction, setInstruction] = useState(null);
    const [updates, setUpdates] = useState({});
    const [clearPoNumber, setClearPoNumber] = useState(false);
    const [omittedUpdates, setOmittedUpdates] = useState({});
    const [carrierClearedUpdates, setCarrierClearedUpdates] = useState({});
    const [duplicateUpdates, setDuplicateUpdates] = useState({
        haulaway_items: [],
        known_availability: false,
        source_form: 'MANUAL',
    });
    const [exchangeUpdates, setExchangedUpdates] = useState({
        haulaway_items: [],
        known_availability: false,
        source_form: 'MANUAL',
    });

    const isAdmin = !!roles['ONWARD_ADMIN'];
    const isShipper = user_id === order?.shipper_id;
    const isCarrier = user_id === order?.carrier_id;

    useEffect(() => {
        setAction(null);
        setInstruction(exception?.instruction);
    }, [exception]);

    const omitted = useMemo(() => {
        if (!order) {
            return {};
        }
        const toOmit = Object.fromEntries((items || []).map((item) => [item.item_id, true]));
        const remaining = (order?.itemsByOrderId || []).filter((item) => !toOmit[item.item_id]);

        return {
            ...order,
            ...omittedUpdates,
            itemsByOrderId: remaining,
            ...(remaining.length === 0 ? { order_status: 'cancelled' } : {}),
        };
    }, [order, items, omittedUpdates]);

    const clonedReferences = useMemo(
        () =>
            (order?.references || [])
                .filter((ref) => ref?.key && ref?.value)
                .map(({ key, value, label }) => ({ key, value, label })),
        [order]
    );

    const itemDelivered = useMemo(() => {
        if (!order) {
            return {};
        }

        return {
            ...pick(order, CLONE_ORDER),
            ...duplicateUpdates,
            references: clonedReferences,
            original_order_id: order.order_id,
            itemsByOrderId: (items || []).map((item) => {
                return pick(item, CLONE_ITEM);
            }),
        };
    }, [order, items, duplicateUpdates, clonedReferences]);

    const itemExchanged = useMemo(() => {
        if (!order) {
            return {};
        }

        return {
            ...pick(order, CLONE_ORDER),
            ...exchangeUpdates,
            original_order_id: order.order_id,
            itemsByOrderId: [
                ...(items || []).map((item) => {
                    return pick(item, CLONE_ITEM);
                }),
                ...(items || []).map((item) => {
                    return { ...pick(item, CLONE_ITEM), is_return: !item.is_return };
                }),
            ],
        };
    }, [order, items, exchangeUpdates]);

    const itemReturned = useMemo(
        () => buildReturnClone(order, { items, clearPoNumber, updates: duplicateUpdates }),
        [order, items, duplicateUpdates, clearPoNumber]
    );

    const whReturned = useMemo(() => {
        if (!order) {
            return {};
        }

        const doAttrs = genAttributes(order, 'DROPOFF');
        const puAttrs = genAttributes(order, 'PICKUP');

        const swapped = {
            ...Object.fromEntries(
                Object.entries(doAttrs).map(([attr, doAttr]) => {
                    return [puAttrs[attr], order[doAttr]];
                })
            ),
            ...Object.fromEntries(
                Object.entries(puAttrs).map(([attr, puAttr]) => {
                    return [doAttrs[attr], order[puAttr]];
                })
            ),
        };

        const event = (order.wh_events || []).find((e) => e.action === 'START:AWAITING');

        return {
            ...pick(order, [...CLONE_ORDER, ...CLONE_MM_FIELDS]),
            ...duplicateUpdates,
            ...(clearPoNumber ? { po_number: null } : {}),
            ...(order.order_type !== 'return' ? swapped : {}),
            warehouse_delivery_status: 'RECEIVED',
            warehouse_actual_received_date: event?.received_date || new Date().toISOString(),
            order_type: 'wh_return',
            special_instructions: order?.order_shipper?.special_instructions_settings?.['Warehouse Return'] || '',
            miidle_mile_carrier_id: order.miidle_mile_carrier_id,
            admin_carrier_rate_override: 0,
            admin_shipper_rate_override: 0,
            order_number: `${order.order_number}-R`,
            reference_id: order.po_number,
            // serial_number/line_id carried here rather than in CLONE_ITEM: itemExchanged maps CLONE_ITEM
            // twice onto one order, and two rows sharing a line_id would violate items_order_line_id_key.
            itemsByOrderId: [
                ...(items || order.itemsByOrderId).map((item) => {
                    return {
                        ...pick(item, CLONE_ITEM),
                        serial_number: item.serial_number,
                        line_id: item.line_id,
                        barcode: item.barcode,
                        is_return: true,
                    };
                }),
            ],
        };
    }, [order, items, duplicateUpdates, clearPoNumber]);

    const carrierCleared = useMemo(() => {
        if (!order) {
            return {};
        }

        const isWarehouse = (order?.wh_events || []).some(({ __typename, exception, transition, ...rest }) => {
            return ['START:AWAITING'].includes(rest.action);
        });

        return {
            ...pick(order, CLONE_ORDER),
            ...carrierClearedUpdates,
            references: clonedReferences,
            itemsByOrderId: [
                ...(order.itemsByOrderId || []).map((item) => {
                    return pick(item, CLONE_ITEM);
                }),
            ],
            carrier_id: null,
            ...(isWarehouse
                ? {
                      is_middle_mile: true,
                  }
                : {}),
            source_form: 'MANUAL',
            po_duplicate_override: 'DUPLICATE',
        };
    }, [order, items, clonedReferences]);

    useEditModalDynamicFields(omitted, setOmittedUpdates);
    useEditModalDynamicFields(itemDelivered, setDuplicateUpdates);
    useEditModalDynamicFields(itemExchanged, setExchangedUpdates);
    useEditModalDynamicFields(carrierCleared, setCarrierClearedUpdates);

    const tz = useMemo(() => {
        if (!order) {
            return 'America/New_York';
        }

        const { zip } = genAttributes(order);
        return zipcode_to_timezone.lookup(order[zip]) || 'America/New_York';
    }, [order]);

    const isValid = useMemo(() => {
        const validInstruction = !REQUIRES_CARRIER_INSTRUCTION.includes(exception?.type) || instruction;
        const validRescheduleReason =
            resolution !== ACTIONS.RESCHEDULE ||
            isRescheduleReasonValid({ reason: updates.reschedule_reason_code, notes: updates.reschedule_reason_notes });
        return validInstruction && validRescheduleReason;
    }, [instruction, exception, resolution, updates]);

    return [
        {
            instruction,
            isAdmin,
            isCarrier,
            isShipper,
            isValid,
            itemDelivered,
            itemExchanged,
            itemReturned,
            whReturned,
            omitted,
            carrierCleared,
            resolution,
            clearPoNumber,
            tz,
            updates,
        },
        {
            setAction: (action) => {
                setAction(action);
                setUpdates({});
                setClearPoNumber(false);
            },
            setUpdates,
            setInstruction,
            setClearPoNumber,
        },
    ];
};

export const useExceptionCallbacks = ({
    callbacks,
    exception,
    instruction,
    itemDelivered,
    itemExchanged,
    itemReturned,
    whReturned,
    carrierCleared,
    items,
    omitted,
    order,
    resolution,
    updates,
    user_id,
}) => {
    const [cloneFailedOrder] = useMutation(CLONE_FAILED_ORDER);
    const [updateOrder] = useMutation(UPDATE_ORDER_BY_ID);
    const client = useApolloClient();

    // Price the attempt clone by its own order_id — pricing reads the clone's wh_events from the DB,
    // which carry exactly the one attempt being billed — and save the rates onto it. Throws on failure
    // so the caller doesn't resolve: the web can't delete an order, so the clone stays unpriced and the
    // exception stays open, and resolving again reprices this same clone (see findUnpricedClone).
    const priceAttemptClone = useCallback(
        async (cloneId, { orderNumber, whEvents }) => {
            let saved = false;
            try {
                if (cloneId) {
                    // wh_events are the clone's own (one attempt) in case pricing can't load them by id.
                    const [, [priced]] = await price([
                        {
                            ...order,
                            order_status: FAILED_ORDER_STATUS,
                            order_id: cloneId,
                            key: cloneId,
                            wh_events: whEvents,
                        },
                    ]);
                    if (priced?.price_breakdown) {
                        await updateOrder({
                            variables: { order_id: cloneId, update: pick(priced, PRICED_RATE_FIELDS) },
                        });
                        saved = true;
                    }
                }
            } catch (e) {
                console.error(e);
            }
            if (!saved) {
                const err = new Error(
                    `Billing order ${orderNumber} was created but could not be priced, so the exception was left open. Resolve it again to retry.`
                );
                captureException(err, { extra: { clone_order_id: cloneId, original_order_id: order?.order_id } });
                throw err;
            }
        },
        [order, updateOrder]
    );

    const resolve = useCallback(() => {
        const last = order?.wh_events?.[order.wh_events.length - 1];
        const status = 'RESOLVED';

        const cancelOnResolve =
            exception.reported_at === 'DROPOFF' &&
            !order.wh_events?.some((event) => !!event.route_id) &&
            status === 'RESOLVED';

        const completeOnResolve =
            [EXCEPTION_TYPES.REFUSAL, EXCEPTION_TYPES.WEATHER, EXCEPTION_TYPES.DAMAGED].includes(exception.type) &&
            exception.reported_at === 'DROPOFF';

        // don't auto complete if the exception is for a partial set of items
        const orderItemCount = order?.itemsByOrderId?.length || 0;
        const exceptionItemCount = items?.length || 0;
        const isPartialItemException =
            exception.type !== EXCEPTION_TYPES.WEATHER &&
            orderItemCount > 0 &&
            exceptionItemCount > 0 &&
            exceptionItemCount < orderItemCount;
        const shouldCompleteOnResolve = completeOnResolve && !isPartialItemException;

        switch (resolution) {
            case ACTIONS.SPLIT_ORDERS: {
                callbacks.setInflight(true);
                return price([omitted]).then(([, [priced]]) => {
                    const updates = Object.fromEntries(
                        ['order_type', 'shipper_rate', 'carrier_rate', 'price_breakdown', 'order_revenue'].map(
                            (attr) => [attr, priced[attr]]
                        )
                    );

                    callbacks.onResolve({
                        toSubmit: itemDelivered,
                        order: {
                            ...updates,
                            order_id: order.order_id,
                            ...(shouldCompleteOnResolve
                                ? {
                                      order_status: 'complete',
                                      completion_source: COMPLETION_SOURCES.WEB,
                                      completion_time: new Date().toISOString(),
                                  }
                                : {}),
                        },
                        exception: {
                            exception_id: exception.exception_id,
                            status,
                            resolution: ACTIONS.SPLIT_ORDERS,
                            instruction,
                        },
                        items,
                    });

                    callbacks.setInflight(false);
                });
            }
            case ACTIONS.CREATE_EXCHANGE: {
                callbacks.setInflight(true);
                return callbacks
                    .onResolve({
                        toSubmit: itemExchanged,
                        order: {
                            order_id: order.order_id,
                            ...(shouldCompleteOnResolve
                                ? {
                                      order_status: 'complete',
                                      completion_source: COMPLETION_SOURCES.WEB,
                                      completion_time: new Date().toISOString(),
                                  }
                                : {}),
                        },
                        exception: {
                            exception_id: exception.exception_id,
                            status,
                            resolution: ACTIONS.CREATE_EXCHANGE,
                            instruction,
                        },
                        items,
                    })
                    .then(() => {
                        callbacks.setInflight(false);
                    });
            }
            case ACTIONS.CREATE_RETURN: {
                callbacks.setInflight(true);
                return callbacks
                    .onResolve({
                        toSubmit: itemReturned,
                        order: {
                            order_id: order.order_id,
                            ...(exception.type === EXCEPTION_TYPES.CUSTOMER_CANCELLATION
                                ? { order_status: 'cancelled' }
                                : shouldCompleteOnResolve
                                ? {
                                      order_status: 'complete',
                                      completion_source: COMPLETION_SOURCES.WEB,
                                      completion_time: new Date().toISOString(),
                                  }
                                : {}),
                        },
                        exception: {
                            exception_id: exception.exception_id,
                            status,
                            resolution: ACTIONS.CREATE_RETURN,
                            instruction,
                        },
                    })
                    .then(() => {
                        callbacks.setInflight(false);
                    });
            }
            case ACTIONS.CREATE_WAREHOUSE_RETURN: {
                callbacks.setInflight(true);
                return callbacks
                    .onResolve({
                        toSubmit: whReturned,
                        order: {
                            order_id: order.order_id,
                            ...(exception.type === EXCEPTION_TYPES.CUSTOMER_CANCELLATION
                                ? { order_status: 'cancelled' }
                                : shouldCompleteOnResolve
                                ? {
                                      order_status: 'complete',
                                      completion_source: COMPLETION_SOURCES.WEB,
                                      completion_time: new Date().toISOString(),
                                  }
                                : {}),
                        },
                        exception: {
                            exception_id: exception.exception_id,
                            status,
                            resolution: ACTIONS.CREATE_RETURN,
                            instruction,
                        },
                        isWhReturn: true,
                    })
                    .then(() => {
                        callbacks.setInflight(false);
                    });
            }
            case ACTIONS.CHANGE_CARRIER: {
                return callbacks.onResolve({
                    toSubmit: carrierCleared,
                    order: {
                        order_id: order.order_id,
                        order_status: 'cancelled',
                    },
                    exception: {
                        exception_id: exception.exception_id,
                        status,
                        resolution: ACTIONS.CHANGE_CARRIER,
                        instruction,
                    },
                });
            }
            case ACTIONS.OMIT_ITEM: {
                callbacks.setInflight(true);
                return price([omitted]).then(([, [priced]]) => {
                    const updates = Object.fromEntries(
                        ['order_type', 'shipper_rate', 'carrier_rate', 'price_breakdown', 'order_revenue'].map(
                            (attr) => [attr, priced[attr]]
                        )
                    );

                    callbacks.onResolve({
                        order: {
                            ...updates,
                            order_id: order.order_id,
                        },
                        exception: {
                            exception_id: exception.exception_id,
                            status,
                            resolution: ACTIONS.OMIT_ITEM,
                            instruction,
                        },
                        items,
                    });

                    callbacks.setInflight(false);
                });
            }
            case ACTIONS.CANCEL_ORDER: {
                const { route_rev_action, custom_charge, recalc_route } = updates;
                return callbacks.onResolve({
                    order: {
                        order_id: order.order_id,
                        order_status: user_id === order.carrier_id ? 'rejected' : 'cancelled',
                    },
                    exception: {
                        exception_id: exception.exception_id,
                        route_id: exception.route_id,
                        status,
                        resolution: ACTIONS.CANCEL_ORDER,
                        instruction,
                        route_rev_action,
                        custom_charge,
                        recalc_route,
                    },
                });
            }
            case ACTIONS.RESOLVE_NOOP: {
                const { route_rev_action, custom_charge, recalc_route } = updates;
                return callbacks.onResolve({
                    order: {
                        order_id: order.order_id,
                        ...(shouldCompleteOnResolve
                            ? {
                                  order_status: 'complete',
                                  completion_source: COMPLETION_SOURCES.WEB,
                                  completion_time: new Date().toISOString(),
                              }
                            : {}),
                    },
                    exception: {
                        exception_id: exception.exception_id,
                        route_id: exception.route_id,
                        status,
                        resolution: ACTIONS.RESOLVE_NOOP,
                        instruction,
                        route_rev_action,
                        custom_charge,
                        recalc_route,
                    },
                });
            }
            case ACTIONS.COMPLETE_ORDER: {
                const { route_rev_action, custom_charge, recalc_route } = updates;
                return callbacks.onResolve({
                    order: {
                        order_id: order.order_id,
                        order_status: 'complete',
                        completion_source: COMPLETION_SOURCES.WEB,
                        completion_time: new Date().toISOString(),
                    },
                    exception: {
                        exception_id: exception.exception_id,
                        route_id: exception.route_id,
                        status,
                        resolution: ACTIONS.COMPLETE_ORDER,
                        instruction,
                        route_rev_action,
                        custom_charge,
                        recalc_route,
                    },
                });
            }
            case ACTIONS.RESCHEDULE: {
                const {
                    pickup_date,
                    delivery_date,
                    route_rev_action,
                    custom_charge,
                    recalc_route,
                    bill_attempt,
                    reschedule_reason_code,
                    reschedule_reason_notes,
                } = updates;

                const rescheduleOrder = {
                    order_id: order.order_id,
                    pickup_date: pickup_date || null,
                    delivery_date: delivery_date || null,
                    del_window_start: null,
                    del_window_end: null,
                    delivery_time_confirmed: null,
                    confirmation_source: null,
                    order_status: !order?.oms && !!order?.carrier_id ? 'claimed' : 'pending',
                    ...buildReschedulePatch({ reason: reschedule_reason_code, notes: reschedule_reason_notes }),
                };
                const rescheduleException = {
                    exception_id: exception.exception_id,
                    route_id: exception.route_id,
                    status,
                    resolution: ACTIONS.RESCHEDULE,
                    instruction,
                    order_id: order.order_id,
                    route_rev_action,
                    custom_charge,
                    recalc_route,
                };

                // "Bill this attempt": clone a terminal "failed" order that bills the exception's attempt,
                // and mark that attempt non-billable on the original.
                if ((bill_attempt ?? true) && exception.type === EXCEPTION_TYPES.ATTEMPTED_DELIVERY) {
                    callbacks.setInflight(true);
                    const currentAttempt = attemptForException(order, exception);

                    // Guard: price the original as a "failed" order before creating anything, so a
                    // missing tariff/attempt fee aborts cleanly. The number itself isn't kept — pricing
                    // reads events from the DB by order_id, so only the clone's own rows price it right.
                    // The clone count drives the -A{n} suffix.
                    return Promise.all([
                        price([
                            {
                                ...order,
                                order_status: FAILED_ORDER_STATUS,
                                key: order.order_id,
                                wh_events: singleAttemptEvents(order, currentAttempt),
                            },
                        ]),
                        client.query({
                            query: ATTEMPT_CLONES_BY_ORIGINAL,
                            variables: { original_order_id: order.order_id },
                            fetchPolicy: 'network-only',
                        }),
                    ])
                        .then(([[, [guard]], cloneCountResult]) => {
                            // Never create a $0/unpriced billing order silently — surface it.
                            if (!guard?.price_breakdown) {
                                captureException(
                                    new Error(`Attempt billing: clone failed to price (order ${order.order_id})`)
                                );
                                throw new Error(
                                    'Could not price this attempt — no billing order was created. Check the tariff/attempt-fee setup and try again.'
                                );
                            }

                            const clones = cloneCountResult?.data?.orders || [];
                            const failedClone = buildFailedClone({
                                order,
                                currentAttempt,
                                existingAttemptClones: clones.length,
                            });
                            // A previous try inserted this attempt's clone but couldn't price it: reuse it.
                            const unpriced = findUnpricedClone(clones, currentAttempt);

                            // Insert the "failed" billing clone here (in the modal) so it works regardless
                            // of which page opened the modal.
                            const cloneReady = unpriced
                                ? Promise.resolve({ cloneId: unpriced.order_id, orderNumber: unpriced.order_number })
                                : cloneFailedOrder({ variables: { order: failedClone } })
                                      .catch((err) => {
                                          err.sentryExtra = {
                                              original_order_id: order.order_id,
                                              failedCloneOrderNumber: failedClone.order_number,
                                              failedClone,
                                          };
                                          throw err;
                                      })
                                      .then(({ data }) => ({
                                          cloneId: data?.order?.order_id,
                                          orderNumber: failedClone.order_number,
                                      }));

                            // Resolve only once the clone exists AND is priced, so the exception is never
                            // closed — nor the original's attempt made non-billable — without a priced
                            // clone billing it. A pricing failure throws here and leaves both untouched.
                            return cloneReady
                                .then(({ cloneId, orderNumber }) =>
                                    priceAttemptClone(cloneId, { orderNumber, whEvents: failedClone.wh_events.data })
                                )
                                .then(() =>
                                    callbacks.onResolve({
                                        order: rescheduleOrder,
                                        exception: rescheduleException,
                                        event: currentAttempt?.event_id
                                            ? { event_id: currentAttempt.event_id, billable: false }
                                            : {},
                                    })
                                );
                        })
                        .finally(() => callbacks.setInflight(false));
                }

                return callbacks.onResolve({
                    order: rescheduleOrder,
                    exception: rescheduleException,
                    ...(last
                        ? {
                              event: {
                                  est_pickup_date: pickup_date || null,
                                  est_delivery_date: delivery_date || null,
                                  event_id: last.order_id,
                              },
                          }
                        : {}),
                });
            }
            case ACTIONS.RETRY_EVENT: {
                callbacks.setInflight(true);
                return post(TRIGGER_ORDER_EVENT, { order_ids: [order.order_id], event: exception.hook_event })
                    .then(() => {
                        callbacks.onResolve({
                            exception: {
                                exception_id: exception.exception_id,
                                status,
                                resolution: ACTIONS.RETRY_EVENT,
                            },
                        });
                    })
                    .finally(() => {
                        callbacks.setInflight(false);
                    });
            }
        }
    }, [
        resolution,
        omitted,
        itemDelivered,
        itemExchanged,
        itemReturned,
        whReturned,
        updates,
        order,
        items,
        exception,
        instruction,
        cloneFailedOrder,
        priceAttemptClone,
        client,
    ]);

    const acknowledge = useCallback(() => {
        callbacks.onResolve({
            order: {
                order_id: order.order_id,
            },
            exception: {
                exception_id: exception.exception_id,
                status: 'RESOLVED',
            },
        });
    }, [order, exception]);

    return { resolve, acknowledge };
};
